import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DeviceListConnectPage from '@components/DeviceListConnectPage';
import DeviceListDetailPage from '@components/DeviceListDetailPage';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { setDeviceCardDetail } from '@store/reducers/userSlice';
import { setUserRole } from '@store/reducers/auth/signupSlice';
import DataLoader from './DataLoader';
import Swal from 'sweetalert2';
import { fetchDeviceInfusionListApi } from '@utils/deviceUtils';

const MySwal = Swal;

const DeviceListComponent: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [showConnectPage, setShowConnectPage] = useState(true);
    const [loading, setLoading] = useState(true);
    const dispatch = useAppDispatch();
    const loggedInUserRole = useAppSelector(
        (state) => state.login.loggedInUserRole
    );
    const accessToken = useAppSelector(
        (state) => state.user.accessToken
    );

    const fetchAttributes = useCallback(async () => {
        try {
            const data = await fetchDeviceInfusionListApi(accessToken);

            if (Array.isArray(data) && data.length > 0) {
                setShowConnectPage(true);
                if (loggedInUserRole) {
                    dispatch(setUserRole(loggedInUserRole));
                }
                dispatch(setDeviceCardDetail(data));
            } else {
                setShowConnectPage(false);
            }
        } catch (error: any) {
            let errorMessage = 'Unexpected error';

            switch (error.name) {
                case 'UserNotFoundException':
                    errorMessage = 'Error: The user does not exist.';
                    break;
                case 'InvalidParameterException':
                    errorMessage = 'Error: An invalid parameter was provided.';
                    break;
                case 'NotAuthorizedException':
                    errorMessage =
                        'Error: You are not authorized to perform this action.';
                    break;
                case 'TooManyRequestsException':
                    errorMessage =
                        'Error: Too many requests. Please try again later.';
                    break;
                case 'InternalErrorException':
                    errorMessage =
                        'Error: An internal error occurred. Please try again later.';
                    break;
                default:
                    errorMessage = error.message;
                    break;
            }

            MySwal.fire({
                position: 'top-end', // Position the toast in the top-right corner
                icon: 'error',
                title: errorMessage,
                showConfirmButton: false,
                timer: 3000,
                toast: true,
                customClass: {
                    popup: '!text-red !bg-light_red', // Add custom class to the popup
                },
                width: 'auto',
            });
            setShowConnectPage(false);
        } finally {
            setLoading(false);
        }
    }, [
        accessToken,
        loggedInUserRole,
        dispatch,
    ]);

    useEffect(() => {
        fetchAttributes();

        const intervalId = setInterval(fetchAttributes, 15000); // 15 seconds

        return () => clearInterval(intervalId); // Cleanup on component unmount
    }, [fetchAttributes]);

    return (
        <>
            {showConnectPage ? (
                <DeviceListDetailPage fetchAttributes={fetchAttributes} />
            ) : (
                <DeviceListConnectPage />
            )}
            {loading && <DataLoader />}
        </>
    );
};
export default DeviceListComponent;
